import { Loader2 } from 'lucide-react';

/**
 * Spinner Component - Loading indicator
 * 
 * @param {Object} props - Component props
 * @param {string} props.size - Spinner size: 'sm', 'md', 'lg', 'xl'
 * @param {string} props.color - Spinner color: 'primary', 'white', 'gray'
 * @param {string} props.className - Additional CSS classes
 */
const Spinner = ({
  size = 'md',
  color = 'primary',
  className = '',
}) => {
  // Size classes
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12',
  };

  // Color classes
  const colorClasses = {
    primary: 'text-primary-600',
    white: 'text-white',
    gray: 'text-gray-400',
    accent: 'text-accent-500',
  };

  return (
    <Loader2 
      className={`
        animate-spin
        ${sizeClasses[size]}
        ${colorClasses[color]}
        ${className}
      `.trim().replace(/\s+/g, ' ')} 
    />
  );
};

/**
 * LoadingOverlay Component - Full-screen loading overlay
 */
export const LoadingOverlay = ({ 
  message = 'Cargando...', 
  transparent = false,
  className = '' 
}) => {
  return (
    <div 
      className={`
        fixed inset-0 flex items-center justify-center z-50
        ${transparent ? 'bg-transparent' : 'bg-white bg-opacity-75'}
        ${className}
      `}
    >
      <div className="text-center">
        <Spinner size="xl" />
        {message && (
          <p className="mt-4 text-gray-600">{message}</p>
        )}
      </div>
    </div>
  );
};

/**
 * LoadingContainer Component - Loading wrapper for content
 */
export const LoadingContainer = ({ 
  loading, 
  children, 
  className = '',
  spinnerSize = 'lg',
  message,
}) => {
  if (!loading) return children;

  return (
    <div className={`relative ${className}`}>
      <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
        <div className="text-center">
          <Spinner size={spinnerSize} />
          {message && <p className="mt-2 text-gray-600 text-sm">{message}</p>}
        </div>
      </div>
      <div className="opacity-30 pointer-events-none">
        {children}
      </div>
    </div>
  );
};

/**
 * ButtonSpinner Component - Spinner for buttons
 */
export const ButtonSpinner = ({ className = '' }) => {
  return (
    <Spinner size="sm" className={`mr-2 ${className}`} />
  );
};

/**
 * PageLoader Component - Full page loading state
 */
export const PageLoader = ({ message = 'Cargando página...' }) => {
  return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="text-center">
        <Spinner size="xl" />
        <p className="mt-4 text-gray-500">{message}</p>
      </div>
    </div>
  );
};

export default Spinner;